
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Route, RefreshCw, MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useRentalMileage } from '@/hooks/useRentalMileage';
import { useAddressCache } from '@/hooks/geocoding/useAddressCache';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { GpsPointWithAddress } from './GpsPointWithAddress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState } from 'react';

interface RentalMileageTabProps {
  rentalId: string;
}

export const RentalMileageTab: React.FC<RentalMileageTabProps> = ({ rentalId }) => {
  const { mileageData, mileagePoints, loading, updateTotalMileage } = useRentalMileage(rentalId);
  const { getAddress, setAddress } = useAddressCache();
  const [isDailyMileageOpen, setIsDailyMileageOpen] = useState(true);
  const [isGpsPointsOpen, setIsGpsPointsOpen] = useState(true);

  const handleUpdateMileage = async () => {
    await updateTotalMileage();
  };

  const handleAddressLoaded = (lat: number, lng: number, address: string) => {
    setAddress(lat, lng, address);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        <span>Загрузка данных пробега...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Общая информация о пробеге */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Route className="h-5 w-5" />
            Общий пробег аренды
          </CardTitle>
          <CardDescription>
            Суммарный пробег, накопленный за время аренды
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="text-2xl font-bold">
                {mileageData?.totalMileage ? `${mileageData.totalMileage} км` : '0 км'}
              </div>
              {mileageData?.lastUpdated && (
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Последнее обновление: {format(new Date(mileageData.lastUpdated), 'dd.MM.yyyy HH:mm', { locale: ru })}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdateMileage}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Пересчитать
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Статистика по дням */}
      {mileageData?.dailyMileage && mileageData.dailyMileage.length > 0 && (
        <Collapsible open={isDailyMileageOpen} onOpenChange={setIsDailyMileageOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Пробег по дням</CardTitle>
                    <CardDescription>
                      Разбивка пробега по дням аренды ({mileageData.dailyMileage.length} {mileageData.dailyMileage.length === 1 ? 'день' : mileageData.dailyMileage.length < 5 ? 'дня' : 'дней'})
                    </CardDescription>
                  </div>
                  {isDailyMileageOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <ScrollArea className="h-48 pr-4">
                  <div className="space-y-2">
                    {mileageData.dailyMileage.map((day, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                        <div className="text-sm font-medium">
                          {format(new Date(day.date), 'dd MMMM yyyy', { locale: ru })}
                        </div>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Route className="h-3 w-3" />
                          {day.mileage} км
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Последние точки GPS с адресами */}
      {mileagePoints && mileagePoints.length > 0 && (
        <Collapsible open={isGpsPointsOpen} onOpenChange={setIsGpsPointsOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Последние GPS точки с адресами</CardTitle>
                    <CardDescription>
                      Показаны последние {Math.min(10, mileagePoints.length)} GPS точек с определением адресов
                    </CardDescription>
                  </div>
                  {isGpsPointsOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <ScrollArea className="h-64 pr-4">
                  <div className="space-y-2">
                    {mileagePoints.slice(-10).reverse().map((point) => (
                      <GpsPointWithAddress
                        key={point.id}
                        point={point}
                        cachedAddress={getAddress(point.latitude, point.longitude)}
                        onAddressLoaded={(address) => handleAddressLoaded(point.latitude, point.longitude, address)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Сообщение, если нет данных */}
      {(!mileagePoints || mileagePoints.length === 0) && (
        <Card>
          <CardContent className="text-center py-8">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Нет данных GPS</h3>
            <p className="text-muted-foreground">
              Для этой аренды пока нет записей о GPS местоположении и пробеге.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
